import React from 'react';
import { Row, Col, Button, Card, Form } from 'react-bootstrap';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import {
  FILE_TYPES,
  IMPORT_ENTITIES,
  IMPORT_RUNNING_STATUS,
} from 'constants/constants';
import { UploadComponent } from 'components/Upload/UploadComponent';
import { Switch } from 'components/Switch';

interface ICustomerImport {
  file: File | undefined;
  path: string;
  importStatus: any;
  handleFileChange: (field: string, files: File[]) => void;
  handleUpload: () => Promise<void>;
  isLoading?: boolean;
  entityType: string;
  setEntityType: (entityType: string) => void;
}

export const ImportCustomerComponent = ({
  file,
  path,
  importStatus,
  handleFileChange,
  handleUpload,
  isLoading,
  entityType,
  setEntityType,
}: ICustomerImport) => {
  const { t } = useTranslation();

  const IMPORT_ENTITIES_OPTIONS = [
    {
      value: IMPORT_ENTITIES.CUSTOMERS,
      label: t('managerManagement.customerLabel'),
    },
    {
      value: IMPORT_ENTITIES.FMCUSTOMERS,
      label: t('managerManagement.fmCustomerLabel'),
    },
  ];

  const handleEntityTypeChange = (type: string) => {
    if (entityType === type) {
      setEntityType(
        type === IMPORT_ENTITIES.CUSTOMERS
          ? IMPORT_ENTITIES.FMCUSTOMERS
          : IMPORT_ENTITIES.CUSTOMERS
      );
    } else {
      setEntityType(type);
    }
  };

  return (
    <>
      <Row className="mb-3 form-control-bordered">
        <Col md={12} className="mt-2">
          <Row className="align-items-center">
            <Col md={12}>
              <Form.Group
                controlId="dealerId"
                className="form-inline form-customer-import"
              >
                <UploadComponent
                  setFieldValue={handleFileChange}
                  accept=".csv"
                  values={file ? [file] : []}
                  fileType={FILE_TYPES.FILE}
                  propField="csvFile"
                  path={path}
                  disable={false}
                />
                <Button
                  onClick={handleUpload}
                  disabled={
                    !file ||
                    isLoading ||
                    importStatus?.status === IMPORT_RUNNING_STATUS.RUNNING
                  }
                  className="ml-3 invite-button"
                >
                  {t('customerManagement.import.uploadButton')}
                </Button>
                <div className="div-switch-row">
                  <Switch
                    id={`entityTypeSwitch${IMPORT_ENTITIES.FMCUSTOMERS}`}
                    name="entityType"
                    label={IMPORT_ENTITIES_OPTIONS[1].label}
                    onChange={() =>
                      handleEntityTypeChange(IMPORT_ENTITIES.FMCUSTOMERS)
                    }
                    checked={entityType === IMPORT_ENTITIES.FMCUSTOMERS}
                    className="div-switch"
                  />
                </div>
                <div className="div-switch-row">
                  <Switch
                    id={`entityTypeSwitch${IMPORT_ENTITIES.CUSTOMERS}`}
                    name="entityType"
                    label={IMPORT_ENTITIES_OPTIONS[0].label}
                    onChange={() =>
                      handleEntityTypeChange(IMPORT_ENTITIES.CUSTOMERS)
                    }
                    checked={entityType === IMPORT_ENTITIES.CUSTOMERS}
                    className="div-switch"
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
        </Col>
      </Row>

      <Card className="mt-4">
        <Card.Body>
          <h3>{t('customerManagement.import.statusArea')}</h3>
          <p>
            <strong>{t('customerManagement.import.status')}</strong>{' '}
            {importStatus &&
              importStatus.status === IMPORT_RUNNING_STATUS.RUNNING && (
                <i className="text-primary">
                  {t('customerManagement.import.running')}
                </i>
              )}
          </p>
          {importStatus &&
            importStatus.status === IMPORT_RUNNING_STATUS.RUNNING && (
              <>
                <p>
                  <strong>{t('customerManagement.import.summary')}</strong>
                </p>
                <ul>
                  <li>
                    {t('customerManagement.import.fileName')}{' '}
                    {importStatus.dataImportLog.fileName
                      ? importStatus.dataImportLog.fileName
                      : 'N/A'}
                  </li>
                  <li>
                    {t('customerManagement.import.importStartTime')}{' '}
                    {importStatus.dataImportLog.startTime
                      ? moment(importStatus.dataImportLog.startTime).format(
                          'LLL'
                        )
                      : 'N/A'}
                  </li>
                </ul>
              </>
            )}
          {importStatus &&
            importStatus.status === IMPORT_RUNNING_STATUS.IDLE &&
            importStatus.dataImportLog && (
              <>
                <p>
                  <strong>
                    {t('customerManagement.import.lastSummaryHeading')}
                  </strong>
                </p>
                <ul>
                  <li>
                    {t('customerManagement.import.fileName')}{' '}
                    {importStatus.dataImportLog.fileName
                      ? importStatus.dataImportLog.fileName
                      : 'N/A'}
                  </li>
                  <li>
                    {t('customerManagement.import.importStartTime')}{' '}
                    {importStatus.dataImportLog.startTime
                      ? moment(importStatus.dataImportLog.startTime).format(
                          'LLL'
                        )
                      : 'N/A'}
                  </li>
                  <li>
                    {t('customerManagement.import.importEndTime')}{' '}
                    {importStatus.dataImportLog.endTime
                      ? moment(importStatus.dataImportLog.endTime).format('LLL')
                      : 'N/A'}
                  </li>
                </ul>
                <p>
                  <strong>{t('customerManagement.import.summary')}</strong>
                </p>
                {importStatus.dataImportLog.importSummary &&
                  importStatus.dataImportLog.importSummary.error && (
                    <p className="text-danger">
                      {importStatus.dataImportLog.importSummary.error}
                    </p>
                  )}
                <ul>
                  <li>
                    {t('customerManagement.import.total')}{' '}
                    {importStatus.dataImportLog.importSummary &&
                      importStatus.dataImportLog.importSummary.total}
                  </li>
                  <li>
                    {t('customerManagement.import.success')}{' '}
                    {importStatus.dataImportLog.importSummary &&
                      importStatus.dataImportLog.importSummary.success}
                  </li>
                  <li>
                    {t('customerManagement.import.duplicate')}{' '}
                    {importStatus.dataImportLog.importSummary &&
                      importStatus.dataImportLog.importSummary.duplicates}
                  </li>
                  <li>
                    {t('customerManagement.import.errors')}{' '}
                    {importStatus.dataImportLog.importSummary &&
                      importStatus.dataImportLog.importSummary.errors}
                  </li>
                </ul>
              </>
            )}
        </Card.Body>
      </Card>
    </>
  );
};

ImportCustomerComponent.defaultProps = {
  isLoading: false,
};
